import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import capitalize from 'lodash/capitalize';
import snakeCase from 'lodash/snakeCase';
import React from 'react';
import { Link as RouteLink } from 'react-router-dom';
import { EMPTY_STATE } from '../PartnerReports.constants';
import { getReportsContainerWidth } from '../PartnerReports.utils';
import { licenseTypesMap } from '../ReportDetails/ReportDetailsConfig/LicenseDetailsConfig';
const entitlementNumberTooltip = 'Entitlement number (EN) is a new ID for the entitlement of this customer. EN will replace SEN as the primary ID. During this transition, the customer can use SEN or EN.';
export const getLicenseReportHeader = enableSenDeprecationChanges => {
  const reportsContainerWidth = getReportsContainerWidth();
  const fieldsToDisplayUponSenDeprecation = [{
    key: 'licenseIdEntitlementNumber',
    content: /*#__PURE__*/React.createElement(React.Fragment, null, "License ID/Entitlement number", /*#__PURE__*/React.createElement(Tooltip, {
      content: /*#__PURE__*/React.createElement(React.Fragment, null, "From Nov 30, 2023, for all cloud licenses, Entitlement Number (EN) has replaced SEN.", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("a", {
        target: "_blank",
        href: "https://atlassianpartners.atlassian.net/wiki/spaces/resources/pages/283312506/Marketplace+Partners%3A+New+date+for+replacement+of+SEN+on+the+new+billing+engine-+Quick+Reference+Guide?atlOrigin=eyJpIjoiZjNjZjZkN2MyNDg3NDIwNmE4MDkzMTE4NGZiNmY2NTEiLCJwIjoiY29uZmx1ZW5jZS1jaGF0cy1pbnQifQ",
        style: {
          color: 'inherit'
        },
        rel: "noreferrer"
      }, /*#__PURE__*/React.createElement("u", null, "Read more")))
    }, /*#__PURE__*/React.createElement(EditorPanelIcon, {
      label: ""
    }))),
    width: reportsContainerWidth * 0.2
  }];
  const fieldsToDisplayBeforeSenDeprecation = [{
    key: 'licenseId',
    content: 'License ID',
    width: reportsContainerWidth * 0.14
  }, {
    key: 'entitlementNumber',
    content: /*#__PURE__*/React.createElement(Tooltip, {
      content: entitlementNumberTooltip
    }, /*#__PURE__*/React.createElement("span", null, "Entitlement number")),
    width: reportsContainerWidth * 0.14
  }];
  /** Divider ReportsContainer width which is equal to 1040px
   * splitted this ReportsContainer width proportionally according to content
   * for each column
   */
  return {
    cells: [{
      key: 'startDate',
      content: 'Start date',
      width: reportsContainerWidth * 0.12
    }, ...(enableSenDeprecationChanges ? fieldsToDisplayUponSenDeprecation : fieldsToDisplayBeforeSenDeprecation), {
      key: 'customer',
      width: reportsContainerWidth * 0.12,
      content: 'Customer'
    }, {
      key: 'app',
      content: 'App',
      width: enableSenDeprecationChanges ? reportsContainerWidth * 0.2 : reportsContainerWidth * 0.16
    }, {
      key: 'license',
      content: 'License',
      width: enableSenDeprecationChanges ? reportsContainerWidth * 0.12 : reportsContainerWidth * 0.1
    }, {
      key: 'status',
      content: 'Status',
      width: enableSenDeprecationChanges ? reportsContainerWidth * 0.12 : reportsContainerWidth * 0.1
    }, {
      key: 'endDate',
      content: 'End date',
      width: reportsContainerWidth * 0.12
    }]
  };
};

// active, cancelled, inactive
const statusLozengeAppearanceMap = {
  active: 'success',
  inactive: '',
  cancelled: 'removed'
};

// eslint-disable-next-line max-lines-per-function
export const getLicenseReportBody = (licenses, getReportsLink, fireEvent, enableSenDeprecationChanges) => {
  if (!licenses) {
    return [];
  }
  const reportsContainerWidth = getReportsContainerWidth();
  const dataAfterSenDeprecation = (license, index) => [{
    key: license.appEntitlementNumber || license.licenseId,
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "license-id-".concat(index + 1)
    }, license.appEntitlementNumber || license.licenseId ? license.licenseType === 'COMMERCIAL' || license.licenseType === 'ACADEMIC' ? /*#__PURE__*/React.createElement(RouteLink, {
      to: getReportsLink('text', license.appEntitlementNumber || license.licenseId, 'licenseId'),
      onClick: () => fireEvent('text')
    }, license.appEntitlementNumber || license.licenseId) : license.appEntitlementNumber || license.licenseId : EMPTY_STATE),
    width: reportsContainerWidth * 0.2
  }];
  const dataBeforeSenDeprecation = (license, index) => [{
    key: license.licenseId,
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "license-id-".concat(index + 1)
    }, license.licenseId ? license.licenseType === 'COMMERCIAL' || license.licenseType === 'ACADEMIC' ? /*#__PURE__*/React.createElement(RouteLink, {
      to: getReportsLink('text', license.licenseId, 'licenseId'),
      onClick: () => fireEvent('text')
    }, license.licenseId) : license.licenseId : EMPTY_STATE),
    width: reportsContainerWidth * 0.14
  }, {
    key: license.appEntitlementNumber,
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "entitlement-number-".concat(index + 1)
    }, license.appEntitlementNumber ? license.licenseType === 'COMMERCIAL' || license.licenseType === 'ACADEMIC' ? /*#__PURE__*/React.createElement(RouteLink, {
      to: getReportsLink('text', license.appEntitlementNumber, 'licenseId'),
      onClick: () => fireEvent('text')
    }, license.appEntitlementNumber) : license.appEntitlementNumber : EMPTY_STATE),
    width: reportsContainerWidth * 0.14
  }];
  // eslint-disable-next-line max-lines-per-function
  return licenses.map((license, index) => {
    return {
      key: license.licenseId,
      itemId: "item".concat(index + 1),
      children: [{
        isChild: true,
        value: license,
        report: 'license'
      }],
      cells: [{
        key: license.maintenanceStartDate,
        content: license.maintenanceStartDate,
        width: reportsContainerWidth * 0.12
      }, ...(enableSenDeprecationChanges ? dataAfterSenDeprecation(license, index) : dataBeforeSenDeprecation(license, index)), {
        key: license.contactDetails.company,
        content: license.contactDetails.company ? /*#__PURE__*/React.createElement(RouteLink, {
          to: getReportsLink('text', license.contactDetails.company),
          onClick: () => fireEvent('text')
        }, license.contactDetails.company) : EMPTY_STATE,
        width: reportsContainerWidth * 0.12
      }, {
        key: license.addonName,
        content: /*#__PURE__*/React.createElement(RouteLink, {
          to: getReportsLink('addon', license.addonKey),
          onClick: () => fireEvent('addon', license.addonKey)
        }, license.addonName),
        width: enableSenDeprecationChanges ? reportsContainerWidth * 0.2 : reportsContainerWidth * 0.16
      }, {
        key: license.licenseType,
        content: licenseTypesMap[snakeCase(license.licenseType)] || capitalize(license.licenseType),
        width: enableSenDeprecationChanges ? reportsContainerWidth * 0.12 : reportsContainerWidth * 0.1
      }, {
        key: license.licenseType,
        content: /*#__PURE__*/React.createElement(Lozenge, {
          appearance: statusLozengeAppearanceMap[license.status]
        }, license.status === 'inactive' ? 'EXPIRED' : license.status),
        width: enableSenDeprecationChanges ? reportsContainerWidth * 0.12 : reportsContainerWidth * 0.1
      }, {
        key: license.maintenanceEndDate,
        content: license.maintenanceEndDate,
        width: reportsContainerWidth * 0.12
      }]
    };
  });
};